import * as THREE from 'three';
import { ProjectilePool } from '../weapons/ProjectilePool';
import { EventBus } from '../core/EventBus';

export interface DroneConfig {
  position: THREE.Vector3;
  targetRef: THREE.Object3D;
  health?: number;
  speed?: number;
  damage?: number;
  fireRate?: number;
}

const DEFAULT_CONFIG = {
  health: 30,
  speed: 15,
  damage: 5,
  fireRate: 1.5, // shots per second
};

let droneIdCounter = 0;

export class FlyingDrone {
  public readonly id: string;
  public readonly mesh: THREE.Group;

  private config: Required<DroneConfig>;
  private projectilePool: ProjectilePool;
  private health: number;
  private isDestroyed: boolean = false;
  private lastFireTime: number = 0;

  // Movement
  private hoverHeight: number;
  private hoverOffset: number = 0;
  private strafeAngle: number = 0;

  // Swarm behavior
  private orbitRadius: number = 8;
  private orbitSpeed: number = 1.5;

  constructor(
    scene: THREE.Scene,
    projectilePool: ProjectilePool,
    config: DroneConfig
  ) {
    this.id = `drone_${droneIdCounter++}`;
    this.config = { ...DEFAULT_CONFIG, ...config } as Required<DroneConfig>;
    this.projectilePool = projectilePool;
    this.health = this.config.health;
    this.hoverHeight = 4 + Math.random() * 3; // Random hover height 4-7m
    this.strafeAngle = Math.random() * Math.PI * 2; // Random start angle

    // Create mesh
    this.mesh = this.createDroneMesh();
    this.mesh.position.copy(config.position);
    this.mesh.position.y = this.hoverHeight;
    scene.add(this.mesh);

    // Set up hit detection
    this.mesh.userData = {
      id: this.id,
      type: 'drone',
      health: this.health,
      hitRadius: 1.5, // Increased from 0.8 for easier targeting
      points: 150,
      isEnemy: true,
      entity: this, // Reference to this drone for damage handling
    };
  }

  private createDroneMesh(): THREE.Group {
    const group = new THREE.Group();

    // Main body - compact sphere
    const bodyGeom = new THREE.SphereGeometry(0.4, 8, 8);
    const bodyMat = new THREE.MeshStandardMaterial({
      color: 0x2a2a4e,
      metalness: 0.9,
      roughness: 0.2,
    });
    const body = new THREE.Mesh(bodyGeom, bodyMat);
    group.add(body);

    // Glowing core
    const coreGeom = new THREE.SphereGeometry(0.2, 8, 8);
    const coreMat = new THREE.MeshStandardMaterial({
      color: 0xff4400,
      emissive: 0xff4400,
      emissiveIntensity: 2,
    });
    const core = new THREE.Mesh(coreGeom, coreMat);
    group.add(core);

    // Rotating ring
    const ringGeom = new THREE.TorusGeometry(0.6, 0.05, 8, 16);
    const ringMat = new THREE.MeshStandardMaterial({
      color: 0x00ffff,
      emissive: 0x00ffff,
      emissiveIntensity: 1.5,
    });
    const ring = new THREE.Mesh(ringGeom, ringMat);
    ring.rotation.x = Math.PI / 2;
    ring.name = 'ring';
    group.add(ring);

    // Second ring (perpendicular)
    const ring2 = new THREE.Mesh(ringGeom.clone(), ringMat.clone());
    ring2.rotation.z = Math.PI / 2;
    ring2.name = 'ring2';
    group.add(ring2);

    // Small thrusters (4 around the body)
    const thrusterMat = new THREE.MeshStandardMaterial({
      color: 0x00ff88,
      emissive: 0x00ff88,
      emissiveIntensity: 1,
    });

    for (let i = 0; i < 4; i++) {
      const angle = (i / 4) * Math.PI * 2;
      const thruster = new THREE.Mesh(
        new THREE.CylinderGeometry(0.08, 0.12, 0.15, 6),
        thrusterMat
      );
      thruster.position.set(
        Math.cos(angle) * 0.35,
        -0.2,
        Math.sin(angle) * 0.35
      );
      group.add(thruster);
    }

    return group;
  }

  public update(dt: number): void {
    if (this.isDestroyed) return;

    const target = this.config.targetRef;
    if (!target) return;

    // Update hover animation
    this.hoverOffset += dt * 3;
    const hoverY = Math.sin(this.hoverOffset) * 0.3;

    // Orbit around target
    this.strafeAngle += this.orbitSpeed * dt;

    const targetPos = target.position.clone();
    const orbitX = Math.cos(this.strafeAngle) * this.orbitRadius;
    const orbitZ = Math.sin(this.strafeAngle) * this.orbitRadius;

    const desiredPos = new THREE.Vector3(
      targetPos.x + orbitX,
      this.hoverHeight + hoverY,
      targetPos.z + orbitZ
    );

    // Smooth movement towards desired position
    const direction = desiredPos.clone().sub(this.mesh.position);
    const distance = direction.length();

    if (distance > 0.1) {
      direction.normalize();
      const moveSpeed = Math.min(this.config.speed * dt, distance);
      this.mesh.position.addScaledVector(direction, moveSpeed);
    }

    // Face the target
    const lookDir = targetPos.clone().sub(this.mesh.position);
    lookDir.y = 0;
    if (lookDir.length() > 0.1) {
      const targetQuat = new THREE.Quaternion();
      const lookMatrix = new THREE.Matrix4();
      lookMatrix.lookAt(this.mesh.position, targetPos, new THREE.Vector3(0, 1, 0));
      targetQuat.setFromRotationMatrix(lookMatrix);
      this.mesh.quaternion.slerp(targetQuat, 5 * dt);
    }

    // Animate rings
    const ring = this.mesh.getObjectByName('ring') as THREE.Mesh;
    const ring2 = this.mesh.getObjectByName('ring2') as THREE.Mesh;
    if (ring) ring.rotation.z += dt * 5;
    if (ring2) ring2.rotation.x += dt * 3;

    // Attack logic
    const distToTarget = this.mesh.position.distanceTo(targetPos);
    if (distToTarget < 20) {
      this.tryFire(targetPos);
    }
  }

  private tryFire(targetPos: THREE.Vector3): void {
    const now = performance.now();
    const fireInterval = 1000 / this.config.fireRate;

    if (now - this.lastFireTime < fireInterval) return;

    this.lastFireTime = now;

    // Fire at target with slight prediction
    const direction = targetPos.clone().sub(this.mesh.position).normalize();

    // Add some inaccuracy
    direction.x += (Math.random() - 0.5) * 0.15;
    direction.y += (Math.random() - 0.5) * 0.1;
    direction.z += (Math.random() - 0.5) * 0.15;
    direction.normalize();

    this.projectilePool.spawn(
      this.mesh.position.clone(),
      direction,
      40, // speed
      this.config.damage,
      new THREE.Color(0xff4400)
    );
  }

  public takeDamage(damage: number): void {
    if (this.isDestroyed) return;

    this.health -= damage;
    this.mesh.userData.health = this.health;

    // Emit damage event
    EventBus.emit('target:damaged', {
      id: this.id,
      damage,
      healthRemaining: this.health,
    });

    // Flash effect
    this.flashDamage();

    if (this.health <= 0) {
      this.destroy();
    }
  }

  private flashDamage(): void {
    const core = this.mesh.children[1] as THREE.Mesh;
    if (core && core.material) {
      const mat = core.material as THREE.MeshStandardMaterial;
      const originalColor = mat.emissive.clone();
      mat.emissive.setHex(0xffffff);
      mat.emissiveIntensity = 5;

      setTimeout(() => {
        mat.emissive.copy(originalColor);
        mat.emissiveIntensity = 2;
      }, 100);
    }
  }

  private destroy(): void {
    this.isDestroyed = true;

    // Emit destroyed event
    EventBus.emit('target:destroyed', {
      id: this.id,
      position: {
        x: this.mesh.position.x,
        y: this.mesh.position.y,
        z: this.mesh.position.z,
      },
      points: this.mesh.userData.points,
    });

    // Remove mesh
    if (this.mesh.parent) {
      this.mesh.parent.remove(this.mesh);
    }
  }

  public isAlive(): boolean {
    return !this.isDestroyed;
  }

  public getPosition(): THREE.Vector3 {
    return this.mesh.position.clone();
  }

  public getMesh(): THREE.Group {
    return this.mesh;
  }
}

// Spawn a swarm of drones
export function spawnDroneSwarm(
  scene: THREE.Scene,
  projectilePool: ProjectilePool,
  centerPosition: THREE.Vector3,
  targetRef: THREE.Object3D,
  count: number = 3
): FlyingDrone[] {
  const drones: FlyingDrone[] = [];

  for (let i = 0; i < count; i++) {
    const angle = (i / count) * Math.PI * 2;
    const offset = new THREE.Vector3(
      Math.cos(angle) * 5,
      2 + Math.random() * 2,
      Math.sin(angle) * 5
    );

    const drone = new FlyingDrone(scene, projectilePool, {
      position: centerPosition.clone().add(offset),
      targetRef,
    });

    drones.push(drone);
  }

  return drones;
}
